import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Sparkles, User, FileText, Image, Video, Zap } from "lucide-react";

interface TimelineStep {
    id: string;
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    status: "waiting" | "active" | "done";
}

const PIPELINE_STEPS: Omit<TimelineStep, "status">[] = [
    { id: "pending", label: "Na fila", description: "Preparando sua publi...", icon: Sparkles },
    { id: "moderating", label: "Validação", description: "Verificando conteúdo...", icon: Zap },
    { id: "processing", label: "Criando Persona", description: "Montando o influencer ideal pro seu produto", icon: User },
    { id: "generating_image", label: "Gerando Imagem", description: "Criando visual hiper-realista com IA", icon: Image },
    { id: "generating_video", label: "Gerando Vídeos", description: "Produzindo vídeos UGC prontos pra postar", icon: Video },
];

const UGC_FACTS = [
    "Vídeos UGC têm 4x mais engajamento que anúncios tradicionais",
    "93% dos consumidores confiam mais em conteúdo autêntico",
    "Publis com IA custam até 10x menos que influencers tradicionais",
    "O formato 9:16 é o que mais converte no TikTok e Reels",
    "Marcas que usam UGC aumentam conversões em até 29%",
    "A Publee gera sua publi em minutos, sem precisar de estúdio",
    "Seus vídeos ficam disponíveis por 48h — baixe antes que expirem!",
    "Dica: quanto mais detalhada a descrição, melhor o resultado",
];

interface GenerationTimelineProps {
    currentStatus: string;
}

export function GenerationTimeline({ currentStatus }: GenerationTimelineProps) {
    const statusOrder = PIPELINE_STEPS.map(s => s.id);
    const currentIndex = statusOrder.indexOf(currentStatus);

    const steps: TimelineStep[] = PIPELINE_STEPS.map((step, i) => ({
        ...step,
        status: i < currentIndex ? "done" : i === currentIndex ? "active" : "waiting",
    }));

    const randomFact = UGC_FACTS[Math.floor(Date.now() / 8000) % UGC_FACTS.length];

    return (
        <div className="space-y-6">
            {/* Progress Steps */}
            <div className="relative space-y-1">
                {steps.map((step, i) => {
                    const Icon = step.icon;
                    return (
                        <motion.div
                            key={step.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1, duration: 0.4 }}
                            className="relative"
                        >
                            <div className={`flex items-center gap-4 p-3 rounded-xl transition-all duration-500 ${step.status === "active"
                                ? "bg-accent/10 border border-accent/30 shadow-lg shadow-accent/5"
                                : step.status === "done"
                                    ? "bg-primary/5 opacity-70"
                                    : "opacity-40"
                                }`}>
                                {/* Icon */}
                                <div className={`relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${step.status === "active"
                                    ? "bg-accent text-accent-foreground"
                                    : step.status === "done"
                                        ? "bg-primary/20 text-primary"
                                        : "bg-secondary text-muted-foreground"
                                    }`}>
                                    {step.status === "done" ? (
                                        <CheckCircle2 className="w-5 h-5" />
                                    ) : step.status === "active" ? (
                                        <>
                                            <Icon className="w-5 h-5" />
                                            {/* Pulse ring */}
                                            <motion.div
                                                className="absolute inset-0 rounded-full border-2 border-accent"
                                                animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
                                                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                            />
                                        </>
                                    ) : (
                                        <Icon className="w-5 h-5" />
                                    )}
                                </div>

                                {/* Text */}
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-semibold ${step.status === "active" ? "text-accent" : ""
                                        }`}>
                                        {step.label}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {step.description}
                                    </p>
                                </div>

                                {/* Status indicator */}
                                {step.status === "active" && (
                                    <Loader2 className="w-4 h-4 text-accent animate-spin flex-shrink-0" />
                                )}
                            </div>

                            {/* Connector line */}
                            {i < steps.length - 1 && (
                                <div className="absolute left-[28px] top-[52px] w-0.5 h-[4px] bg-border" />
                            )}
                        </motion.div>
                    );
                })}
            </div>

            {/* Fun fact card */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={randomFact}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4 }}
                    className="p-4 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20"
                >
                    <div className="flex items-start gap-3">
                        <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-xs font-medium text-primary mb-1">Você sabia?</p>
                            <p className="text-xs text-muted-foreground leading-relaxed">{randomFact}</p>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
